import { useCallback, useEffect, useState } from 'react';
import { useDIContext } from '@/Framework/DI/DIContext';
import { useTopCompanyCommonContext } from '@/finsight/application/TopCompanies/topCompaniesCommon/TopCompaniesCommonContext';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';
import { prepareTopCompanyPayload } from '@/finsight/application/TopCompanies/helpers';
import { DEFAULT_PERIOD } from '@/finsight/application/TopCompanies/constants';
import CompanyRepository from '@/finsight/infrastructure/repository/CompanyRepository';
import useFetchCollection from '@/Framework/State/useFetchCollection';
import { ISSUER_NAME, PARENT_NAME } from '@/finsight/ui/common/components/TopCompanies/constants';
import { ITopCompany, ITopCompanyServerData } from '@/finsight/application/TopCompanies/interfaces';

interface ICompanyServerData {
  serverData?: ITopCompanyServerData,
  companyRole: string,
}

const useTopCompany = ({
  serverData,
  companyRole,
}: ICompanyServerData) => {
  const { container } = useDIContext();
  const { setIsExportAvailable, selectedPeriod, activeTab } = useTopCompanyCommonContext();
  const [showVolume, setShowVolume] = useState<boolean>(serverData?.showVolume || false);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!!serverData?.topCompanies.collection);
  const { pageFilters } = usePageFiltersContext();
  const isTabActive = companyRole === PARENT_NAME ? activeTab === ISSUER_NAME : activeTab === companyRole;

  const payload = prepareTopCompanyPayload({
    pageFilters,
    perPage: 9,
    dateFrom: selectedPeriod?.dateFrom,
    dateTo: selectedPeriod?.dateTo,
    period: selectedPeriod?.value || DEFAULT_PERIOD,
    includedCompanyRole: companyRole,
  });

  const companyRepository = container.get<CompanyRepository>(CompanyRepository);

  const fetchTopCompanies = useCallback(
      async (payload) => {
        const { search, sortBy, sortOrder, ...other } = payload;
        const response = await companyRepository.getTopCompanies(other);
        setIsExportAvailable(response.showExport);
        setShowVolume(response.showVolume);
        setIsFirstFetching(true);
        return response.topCompanies;
      },
      [],
  );

  useEffect(() => () => {
    topCompaniesListing.softReset();
  }, [pageFilters, selectedPeriod]);

  const topCompaniesListing = useFetchCollection<ITopCompany>(
      isFirstFetching || !isTabActive ? null : fetchTopCompanies,
      payload,
      {
        perPage: payload.perPage,
        totalCount: serverData?.topCompanies.totalCount,
        collection: serverData?.topCompanies.collection || [],
      });

  useEffect(() => () => {
    setIsFirstFetching(false);
  }, [
    selectedPeriod,
    topCompaniesListing.page,
  ]);

  return {
    ...topCompaniesListing,
    showVolume,
  };
};

export default useTopCompany;
